// Remove Location Data's confirm (DLG-132; plan 2026-10-13-remove-location-data; legacy AccountLocations.vue's
// UiUnsavedChangesDialog with `captcha`, 550 wide, a red Proceed): "Clear Location Information", the location's name in
// bold, a random 6-character word to type back, then `tenancy.clearLocationData`. As in legacy, Proceed checks the word
// when pressed and says what is wrong under the field. In a multi-location account the confirm also says what stays:
// Radish Next's account-shared items, categories, modifier sets and discounts, and every department, which other
// locations sell too (`tenancy.locationData.preview`; ledger L451). Legacy had only per-location copies and wiped them.
import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Dialog, TextField, useToast } from '@radish/ui-web';
import { unwrap, useApi, useLocationQuery, type CallFailure } from '../../data/api.tsx';
import { uuidv7 } from '../../lib/ids.ts';

const CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

/** Legacy `generateAlphanumericString(6)`. */
export function captchaWord(random: () => number = Math.random): string {
  return Array.from({ length: 6 }, () => CHARACTERS[Math.floor(random() * CHARACTERS.length)] ?? 'A').join('');
}

type Kept = { readonly items: number; readonly variants: number; readonly categories: number; readonly modifierSets: number; readonly discounts: number; readonly departments: number };

const counted = (n: number, one: string, many: string): string | null => (n === 0 ? null : `${String(n)} ${n === 1 ? one : many}`);

/** "Shared with other locations, kept: 42 items, 6 categories, all departments", or null when nothing is kept. */
export function keptSentence(kept: Kept): string | null {
  const parts = [
    counted(kept.items, 'item', 'items'), counted(kept.categories, 'category', 'categories'), counted(kept.modifierSets, 'modifier set', 'modifier sets'),
    counted(kept.discounts, 'discount', 'discounts'), kept.departments === 0 ? null : 'all departments',
  ].filter((p): p is string => p !== null);
  return parts.length === 0 ? null : `Shared with other locations, kept: ${parts.join(', ')}`;
}

/** Legacy BaseInput's rules, in order. */
export function captchaError(typed: string, word: string): string | null {
  if (typed === '') return 'Please enter the 6 letter word displayed above';
  if (typed !== word) return 'Please make the 6 letter word displayed above match the word displayed above';
  return null;
}

export function ClearLocationDataDialog({ accountId, location, onClose, onCleared, random }: {
  readonly accountId: string;
  readonly location: { readonly locationId: string; readonly name: string } | null;
  readonly onClose: () => void;
  readonly onCleared: () => void;
  readonly random?: () => number;
}) {
  const { client } = useApi();
  const toast = useToast();
  const preview = useLocationQuery(accountId, 'tenancy.locationData.preview', { locationId: location?.locationId }, (c) => c.queries.tenancy.locationData.preview({ locationId: (location?.locationId ?? '') as never }), location !== null);
  const kept = preview.data === undefined ? null : keptSentence(preview.data.kept);
  const word = useMemo(() => captchaWord(random), [location?.locationId, random]);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const clear = useMutation<unknown, CallFailure, string>({
    mutationFn: (locationId) => {
      const commandId = uuidv7();
      return unwrap(() => client.commands.tenancy.clearLocationData({ locationId: locationId as never }, { commandId }));
    },
  });
  const close = () => { setTyped(''); setError(null); onClose(); };
  const proceed = () => {
    if (location === null) return;
    const problem = captchaError(typed, word);
    setError(problem);
    if (problem !== null) return;
    clear.mutate(location.locationId, {
      onSuccess: () => { close(); onCleared(); },
      onError: (e) => toast.error(e.message),
    });
  };
  return (
    <Dialog
      open={location !== null}
      onOpenChange={(o) => { if (!o) close(); }}
      title="Clear Location Information"
      width={550}
      testId="clear-location-data-dialog"
      footer={<><Button variant="outline" onClick={close}>Cancel</Button><Button variant="negative" loading={clear.isPending} onClick={proceed}>Proceed</Button></>}
    >
      <form className="flex flex-col gap-2" autoComplete="off" onSubmit={(e) => { e.preventDefault(); proceed(); }}>
        <p className="text-[14px] text-text-2">
          This action will remove all menu, discounts and settings data from the location <span className="text-[18px] font-bold">{location?.name}</span>. Please note that this action is not reversible
        </p>
        {kept === null ? null : <p className="text-[14px] font-medium text-text-1" data-testid="clear-location-data-kept">{kept}</p>}
        <p className="py-2 text-[24px] font-bold text-black" data-testid="clear-location-data-word">{word}</p>
        <TextField
          label="Type the 6 letter word displayed above" value={typed} maxLength={6} autoCapitalize="off" autoCorrect="off" spellCheck={false}
          onValueChange={(v) => { setTyped(v); if (error !== null) setError(captchaError(v, word)); }} error={error ?? undefined}
        />
      </form>
    </Dialog>
  );
}
